import React from 'react';
import Link from 'next/link';
import { ComponentNode } from '../types/template';
import { convertStylesToInline, getCustomThemeItemsLayoutStyle, isResponsiveSidebar } from '../utils/styleUtils';
import { ApiRequestConfig, DataFetcher, fetchRawDataForRenderRequest } from '../utils/DataFetcher';
import ComponentRenderer from './ComponentRenderer';
import MerchantSectionSlider from './MerchantSectionSlider';
import {
    buildCategoryHref,
    buildEventHref,
    buildMerchantHref,
    buildProductHref,
    buildPromotionHref,
} from '../utils/linkHelpers';
import { getNestedValue } from '../utils/dataBinding';
import { buildRequestConfig, getRouteTemplateVariables } from '../utils/templateVariables';
import { getBaseImageUrl } from '@/constants/hooks';
import LoadMoreCardList from './LoadMoreCardList';
import PaginatedCardList from './PaginatedCardList';
import EmptyStateComponent, { getEmptyStateKind } from './EmptyStateComponent';

interface RenderContext {
    companyId?: string;
    templateId?: string;
    domain?: string;
    pageSlug?: string;
    params?: Record<string, any>;
    searchParams?: Record<string, any>;
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
    out_link?: string;
}

interface DynamicSectionComponentProps {
    component: ComponentNode;
    context?: RenderContext;
}

const hasDescendantDataSourceKey = (node: ComponentNode | undefined, key: string): boolean => {
    if (!node) {
        return false;
    }

    if (node.dataSourceKey === key) {
        return true;
    }

    return Array.isArray(node.components)
        ? node.components.some((child) => hasDescendantDataSourceKey(child, key))
        : false;
};

const arrayFromUnknown = (value: any): any[] => {
    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value?.data)) {
        return value.data;
    }

    return [];
};

const collectArrays = (value: any): any[] => {
    if (Array.isArray(value)) {
        return value;
    }

    if (!value || typeof value !== 'object') {
        return [];
    }

    const collected: any[] = [];

    Object.values(value).forEach((nested) => {
        const nestedItems = collectArrays(nested);
        if (nestedItems.length > 0) {
            collected.push(...nestedItems);
        }
    });

    return collected;
};

interface LinkBuilderConfig {
    store_slug?: string;
    slug_type?: string;
    category_slug?: string;
    promotion_slug?: string;
    out_link?: string;
    domain?: string;
}

// Some endpoints (e.g. triple banners) return image paths relative to the media
// host (e.g. "uploads/company_5/images/x.jpg") rather than a full URL.
const resolveImageUrl = (domain: string | undefined, src: string): string => {
    if (!src || !domain || /^(https?:)?\/\//i.test(src)) {
        return src;
    }
    return getBaseImageUrl(domain, src, src);
};

// Field names that carry an image path on any nested sub-object (merchant,
// category, etc.) a JSON node might bind into directly via a dotted
// placeholder like "merchant.merchant_logo" - kept generic (name-based, not
// tied to any one API response shape) so it helps any current/future nested
// object, not just combined-offers' merchant.
const NESTED_IMAGE_FIELDS = ['merchant_logo', 'logo', 'image', 'icon', 'category_image', 'featured_image', 'thumbnail'];

const normalizeNestedImages = (nested: Record<string, any>, domain: string | undefined): Record<string, any> => {
    const patched: Record<string, any> = { ...nested };
    NESTED_IMAGE_FIELDS.forEach((field) => {
        if (typeof patched[field] === 'string' && patched[field]) {
            patched[field] = resolveImageUrl(domain, patched[field]);
        }
    });
    return patched;
};

const getLinkBuilderConfig = (responseData: any, context?: RenderContext): LinkBuilderConfig => {
    const candidates = [
        responseData?.data?.config,
        responseData?.config,
        responseData?.data?.company_data,
        responseData?.company_data,
        responseData?.data?.company,
        responseData?.company,
        responseData?.data,
        responseData,
    ];

    let candidateConfig: LinkBuilderConfig = {};
    for (const candidate of candidates) {
        if (
            candidate &&
            typeof candidate === 'object' &&
            (candidate.store_slug || candidate.slug_type || candidate.category_slug || candidate.promotion_slug || candidate.out_link)
        ) {
            candidateConfig = {
                store_slug: candidate.store_slug,
                slug_type: candidate.slug_type,
                category_slug: candidate.category_slug,
                promotion_slug: candidate.promotion_slug,
                out_link: candidate.out_link,
            };
            break;
        }
    }

    return {
        store_slug: context?.store_slug || candidateConfig.store_slug,
        slug_type: context?.slug_type || candidateConfig.slug_type,
        category_slug: context?.category_slug || candidateConfig.category_slug,
        promotion_slug: context?.promotion_slug || candidateConfig.promotion_slug,
        out_link: context?.out_link || candidateConfig.out_link,
        domain: context?.domain,
    };
};

const expandGroupedOffers = (value: any): any[] => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return [];
    }

    const groupedEntries = Object.values(value).filter(
        (entry) => entry && typeof entry === 'object' && Array.isArray((entry as any).offers)
    ) as Array<{
        merchant?: Record<string, any>;
        offers: Array<Record<string, any>>;
        home_page_widget?: Record<string, any> | null;
    }>;

    if (groupedEntries.length === 0) {
        return [];
    }

    return groupedEntries.flatMap((entry) => {
        const merchant = entry.merchant || {};
        const widget = entry.home_page_widget || {};

        return entry.offers.map((offerWrapper) => {
            const nestedOffer = offerWrapper.offer || {};

            return {
                ...widget,
                ...merchant,
                ...offerWrapper,
                ...nestedOffer,
                merchant,
                home_page_widget: entry.home_page_widget,
                offer: nestedOffer,
                merchant_name: merchant.merchant_name || merchant.name || '',
                merchant_logo: merchant.merchant_logo || merchant.logo || '',
                merchant_slug: merchant.slug || '',
                promotional_tag: merchant.promotional_tag || '',
                widget_heading: widget.widget_heading || '',
                widget_text: widget.widget_text || '',
                offer_title: nestedOffer.offer_title || offerWrapper.offer_title || '',
                offer_detail: nestedOffer.offer_detail || '',
                product_image: nestedOffer.product_image || '',
                banner_image: nestedOffer.banner_image || '',
                offer_type_name: nestedOffer.offer_type?.name || '',
                offer_url: nestedOffer.url || offerWrapper.url || '#',
                read_more_url: nestedOffer.url || offerWrapper.url || '#',
            };
        });
    });
};

const expandGroupedEntry = (entry: any): any[] => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry) || !Array.isArray(entry.offers)) {
        return [];
    }

    const merchant = entry.merchant || {};
    const widget = entry.home_page_widget || {};

    return entry.offers.map((offerWrapper: any) => {
        const nestedOffer = offerWrapper.offer || {};

        return {
            ...widget,
            ...merchant,
            ...offerWrapper,
            ...nestedOffer,
            merchant,
            home_page_widget: entry.home_page_widget,
            offer: nestedOffer,
            merchant_name: merchant.merchant_name || merchant.name || '',
            merchant_logo: merchant.merchant_logo || merchant.logo || '',
            merchant_slug: merchant.slug || '',
            promotional_tag: merchant.promotional_tag || '',
            widget_heading: widget.widget_heading || '',
            widget_text: widget.widget_text || '',
            offer_title: nestedOffer.offer_title || offerWrapper.offer_title || '',
            offer_detail: nestedOffer.offer_detail || '',
            product_image: nestedOffer.product_image || '',
            banner_image: nestedOffer.banner_image || '',
            offer_type_name: nestedOffer.offer_type?.name || '',
            offer_url: nestedOffer.url || offerWrapper.url || '#',
            read_more_url: nestedOffer.url || offerWrapper.url || '#',
        };
    });
};

const normalizeGroupedEntry = (entry: any, config: LinkBuilderConfig) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry) || !Array.isArray(entry.offers)) {
        return null;
    }

    const merchant = entry.merchant || {};
    const widget = entry.home_page_widget || {};
    const normalizedMerchant = normalizeItem(merchant, config);

    return {
        ...widget,
        ...merchant,
        ...normalizedMerchant,
        merchant,
        home_page_widget: entry.home_page_widget,
        section_heading: widget.widget_heading || '',
        widget_heading: widget.widget_heading || '',
        widget_text: widget.widget_text || '',
        merchant_name: merchant.merchant_name || merchant.name || '',
        merchant_logo: merchant.merchant_logo || merchant.logo || '',
        merchant_slug: merchant.slug || '',
        promotional_tag: merchant.promotional_tag || '',
        offers: entry.offers.map((offerWrapper: any) =>
            normalizeItem(
                {
                    ...widget,
                    ...merchant,
                    ...offerWrapper,
                    merchant,
                    home_page_widget: entry.home_page_widget,
                },
                config
            )
        ),
    };
};

const normalizeItem = (item: any, config: LinkBuilderConfig) => {
    if (!item || typeof item !== 'object') {
        return item;
    }

    const categoryData = item.category && typeof item.category === 'object' ? item.category : {};
    const merchantData = item.merchant && typeof item.merchant === 'object' ? item.merchant : {};
    const nestedOffer = item.offer && typeof item.offer === 'object' ? item.offer : {};
    const widgetData = item.home_page_widget && typeof item.home_page_widget === 'object' ? item.home_page_widget : {};
    const mergedItem = {
        ...widgetData,
        ...categoryData,
        ...merchantData,
        ...item,
        ...nestedOffer,
    };

    const slug = mergedItem.slug || mergedItem.url_slug || mergedItem.category_slug || mergedItem.store_slug;
    const resolvedImage = resolveImageUrl(config.domain, (
        mergedItem.image ||
        mergedItem.product_image ||
        mergedItem.offer_image ||
        mergedItem.featured_image ||
        mergedItem.thumbnail ||
        mergedItem.banner_image ||
        mergedItem.slider_image ||
        mergedItem.icon ||
        ''
    ));

    const merchantUrl = mergedItem.merchant_url || buildMerchantHref(mergedItem, config) || '#';
    const categoryUrl = mergedItem.category_url || mergedItem.url || buildCategoryHref(mergedItem, config) || '#';
    const eventUrl = mergedItem.event_url || mergedItem.url || buildEventHref(mergedItem) || '#';
    const promotionUrl = mergedItem.promotion_url || mergedItem.url || buildPromotionHref(mergedItem, config) || '#';
    const productUrl = mergedItem.product_url || mergedItem.url || buildProductHref(mergedItem, config) || '#';
    const offerUrl = mergedItem.offer_url || mergedItem.url || '#';
    const defaultUrl =
        mergedItem.url ||
        mergedItem.link ||
        mergedItem.button_link ||
        merchantUrl ||
        categoryUrl ||
        productUrl ||
        promotionUrl ||
        eventUrl ||
        offerUrl ||
        '#';
    const originalPrice = Number(mergedItem.original_price);
    const salePrice = Number(mergedItem.sale_price);
    const computedDiscount =
        Number.isFinite(originalPrice) &&
            Number.isFinite(salePrice) &&
            originalPrice > 0 &&
            salePrice >= 0 &&
            salePrice < originalPrice
            ? `${Math.round(((originalPrice - salePrice) / originalPrice) * 100)}% OFF`
            : '';

    return {
        ...mergedItem,
        image: resolvedImage,
        // resolvedImage already checks slider_image (and domain-prefixes relative paths) -
        // don't fall back to the raw mergedItem.slider_image here, it would skip that prefixing.
        slide_image: mergedItem.slide_image || resolvedImage || mergedItem.banner_image || '',
        slide_title: mergedItem.slide_title || mergedItem.offer_title || mergedItem.title || mergedItem.text || mergedItem.name || '',
        slide_subtitle: mergedItem.slide_subtitle || mergedItem.subtitle || mergedItem.description || mergedItem.offer_detail || '',
        cta_text: mergedItem.cta_text || mergedItem.button_text || mergedItem.cta_label || 'Read More',
        blog_title: mergedItem.blog_title || mergedItem.offer_title || mergedItem.title || mergedItem.name || '',
        blog_date: mergedItem.blog_date || mergedItem.date || mergedItem.created_at || '',
        blog_image: mergedItem.blog_image || resolvedImage || mergedItem.featured_image || '',
        blog_summary: mergedItem.blog_summary || mergedItem.summary || mergedItem.content || mergedItem.text || mergedItem.offer_detail || '',
        url: mergedItem.url || defaultUrl,
        link: mergedItem.link || defaultUrl,
        redirect_url: mergedItem.redirect_url || defaultUrl,
        read_more_url: mergedItem.read_more_url || defaultUrl,
        category_name: mergedItem.category_name || mergedItem.name || mergedItem.title || '',
        subcategory_name: mergedItem.subcategory_name || mergedItem.name || mergedItem.title || '',
        category_image: mergedItem.category_image || resolvedImage || mergedItem.icon || '',
        category_url: categoryUrl,
        child: Array.isArray(mergedItem.child) ? mergedItem.child : Array.isArray(categoryData.child) ? categoryData.child : [],
        merchant_logo: resolveImageUrl(config.domain, mergedItem.merchant_logo || mergedItem.logo || '') || resolvedImage || '',
        merchant_name: mergedItem.merchant_name || mergedItem.store_name || mergedItem.name || '',
        merchant_url: merchantUrl,
        section_heading: mergedItem.section_heading || mergedItem.widget_heading || '',
        store_name: mergedItem.store_name || mergedItem.merchant_name || mergedItem.name || '',
        event_url: eventUrl,
        promotion_url: promotionUrl,
        product_url: productUrl,
        offer_url: offerUrl,
        discount: mergedItem.discount || computedDiscount,
        total_offers:
            mergedItem.total_offers ||
            mergedItem.active_offers ||
            (typeof mergedItem.offers_count !== 'undefined' ? `${mergedItem.offers_count} Active Offers` : ''),
        question: mergedItem.question || '',
        answer: mergedItem.answer || '',
        // A JSON node may bind directly into a still-nested sub-object (e.g.
        // "merchant.merchant_logo", "offer.offer_title") rather than one of
        // the flattened aliases above - keep those sub-objects on the
        // returned item (generic response shapes like get_custom_offers_slug's
        // featured_items[].merchant/.offer rely on this), but with their own
        // known image fields domain-prefixed the same way the flattened
        // aliases are, since the plain ImageComponent never calls
        // getBaseImageUrl itself.
        ...(merchantData && Object.keys(merchantData).length > 0
            ? { merchant: normalizeNestedImages(merchantData, config.domain) }
            : {}),
        ...(nestedOffer && Object.keys(nestedOffer).length > 0 ? { offer: nestedOffer } : {}),
    };
};

const resolveCollection = (responseData: any, component: ComponentNode, context?: RenderContext): any[] => {
    const linkBuilderConfig = getLinkBuilderConfig(responseData, context);
    const dataSourceKey = component.dataSourceKey;

    // Combined builder API always has data.slug_type — handle it before any generic
    // fallback that might grab the wrong nested array (e.g. data.event.offers for event slug).
    // Requires a non-empty array: get_custom_offers_slug's response always carries an
    // (often empty) data.items alongside the real collection under a different key
    // (data.featured_items) — an empty items must not short-circuit past the
    // dataSourceKey branch below that would find it.
    if (Array.isArray(responseData?.data?.items) && responseData.data.items.length > 0 && responseData?.data?.slug_type) {
        // Build a merchant lookup so flat event-offer items (which only carry merchant_id)
        // can be enriched with merchant_name, merchant_logo, slug, etc.
        const merchantsLookup: Record<number, any> = {};
        if (Array.isArray(responseData?.data?.merchants)) {
            responseData.data.merchants.forEach((m: any) => {
                if (m?.id) merchantsLookup[m.id] = m;
            });
        }
        const singleMerchant =
            responseData?.data?.merchant && typeof responseData.data.merchant === 'object'
                ? responseData.data.merchant
                : undefined;
        return responseData.data.items.map((item: any) => {
            const merchant =
                item.merchant && typeof item.merchant === 'object'
                    ? item.merchant
                    : merchantsLookup[item.merchant_id] || singleMerchant || {};
            const offer = item.offer && typeof item.offer === 'object' ? item.offer : item;
            return normalizeItem({ ...item, merchant, offer }, linkBuilderConfig);
        });
    }

    // Custom builder FAQs: { data: { faq_type, faqs: [...], merchant/category: {...} } }
    if (Array.isArray(responseData?.data?.faqs)) {
        return responseData.data.faqs.map((item: any) => normalizeItem(item, linkBuilderConfig));
    }

    // Special handling for event offers: flatten all offers from all merchants
    const merchants = responseData?.data?.merchants;
    if (Array.isArray(merchants)) {
        const allOffers: any[] = [];
        merchants.forEach((merchant: any) => {
            if (Array.isArray(merchant.offers)) {
                merchant.offers.forEach((offer: any) => {
                    allOffers.push({
                        ...merchant,
                        ...offer,
                        merchant,
                        offer,
                        merchant_name: merchant.merchant_name || merchant.name || '',
                        merchant_logo: merchant.merchant_logo || merchant.logo || '',
                        merchant_slug: merchant.slug || '',
                        offer_title: offer.offer_title || '',
                        offer_detail: offer.offer_detail || '',
                        offer_url: offer.url || '#',
                        url: offer.url || '#',
                        read_more_url: offer.url || '#',
                    });
                });
            }
        });
        if (allOffers.length > 0) {
            return allOffers.map((item) => normalizeItem(item, linkBuilderConfig));
        }
    }

    const groupedEntries = !Array.isArray(responseData?.data) && responseData?.data && typeof responseData.data === 'object'
        ? Object.values(responseData.data).filter(
            (entry) => entry && typeof entry === 'object' && Array.isArray((entry as any).offers)
        )
        : [];

    if (dataSourceKey) {
        const keyedData =
            getNestedValue(responseData, dataSourceKey) ??
            getNestedValue(responseData?.data, dataSourceKey) ??
            getNestedValue(responseData?.data?.[0], dataSourceKey);

        const expandedKeyedGroup = expandGroupedEntry(keyedData);
        if (expandedKeyedGroup.length > 0) {
            return expandedKeyedGroup.map((item) => normalizeItem(item, linkBuilderConfig));
        }

        const keyedArray = arrayFromUnknown(keyedData);
        if (keyedArray.length > 0) {
            return keyedArray.map((item) => normalizeItem(item, linkBuilderConfig));
        }
    }

    if (groupedEntries.length > 0 && hasDescendantDataSourceKey(component, 'offers')) {
        return groupedEntries
            .map((entry) => normalizeGroupedEntry(entry, linkBuilderConfig))
            .filter(Boolean) as any[];
    }

    const expandedGroupedOffers = expandGroupedOffers(responseData?.data);

    if (expandedGroupedOffers.length > 0) {
        return expandedGroupedOffers.map((item) => normalizeItem(item, linkBuilderConfig));
    }

    if (Array.isArray(responseData)) {
        return responseData.map((item) => normalizeItem(item, linkBuilderConfig));
    }

    const directArray = arrayFromUnknown(responseData);
    if (directArray.length > 0) {
        // Some endpoints (e.g. triple banners) return an array of grouped arrays -
        // [[item, item, item], [item, item, item], ...] - flatten one level so
        // normalizeItem always receives individual items, not whole groups.
        const flatArray = directArray.every((entry) => Array.isArray(entry))
            ? directArray.flat()
            : directArray;
        return flatArray.map((item) => normalizeItem(item, linkBuilderConfig));
    }

    const nestedArray = collectArrays(responseData?.data);
    if (nestedArray.length > 0) {
        return nestedArray.map((item) => normalizeItem(item, linkBuilderConfig));
    }

    return [];
};

const resolveSectionMeta = (response: any, context?: RenderContext) => {
    const baseMeta =
        response?.data && !Array.isArray(response.data) ? response.data : response;

    if (!baseMeta || typeof baseMeta !== 'object' || Array.isArray(baseMeta)) {
        return baseMeta;
    }

    const widgetMeta = Object.entries(baseMeta).reduce<Record<string, any>>((acc, [key, value]) => {
        if (
            key.endsWith('_widget') &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value)
        ) {
            Object.assign(acc, value);
        }

        return acc;
    }, {});

    const configMeta =
        context?.store_slug || context?.slug_type || context?.category_slug || context?.promotion_slug || context?.out_link
            ? {
                config: {
                    ...(baseMeta.config && typeof baseMeta.config === 'object' ? baseMeta.config : {}),
                    ...(context?.store_slug ? { store_slug: context.store_slug } : {}),
                    ...(context?.slug_type ? { slug_type: context.slug_type } : {}),
                    ...(context?.category_slug ? { category_slug: context.category_slug } : {}),
                    ...(context?.promotion_slug ? { promotion_slug: context.promotion_slug } : {}),
                    ...(context?.out_link ? { out_link: context.out_link } : {}),
                },
            }
            : {};

    return {
        ...baseMeta,
        ...widgetMeta,
        ...configMeta,
    };
};

const buildAllStoresBaseUrl = (context?: RenderContext) => {
    const slug = context?.params?.slug;
    if (Array.isArray(slug) && slug[0] === 'all-stores' && slug[1]) {
        return `/all-stores/${slug[1]}`;
    }

    return '/all-stores/A';
};

const applyRouteAwareRequestOverrides = (
    requestConfig: ApiRequestConfig | undefined,
    context?: RenderContext
): ApiRequestConfig | undefined => {
    if (!requestConfig) {
        return undefined;
    }

    const normalizedEndpoint = requestConfig.apiEndpoint || '';
    const vars = getRouteTemplateVariables(context);

    if (normalizedEndpoint.includes('get_merchants_by_alphabet')) {
        const alphabet = vars.alphabet || 'A';
        const page = Number(vars.page || '1') || 1;
        // Match the existing Theme-4 all-stores page, which uses 30 merchants
        // per request and relies on the API's pagination metadata for the pager.
        const configuredPerPage = Number(requestConfig.body?.per_page);
        const perPage = Number.isFinite(configuredPerPage) && configuredPerPage > 0
            ? Math.floor(configuredPerPage)
            : 30;

        return {
            ...requestConfig,
            body: {
                ...(requestConfig.body ?? {}),
                alphabet: alphabet === 'other' ? '#' : alphabet,
                page,
                per_page: perPage,
            },
            params: {
                ...(requestConfig.params ?? {}),
            },
        };
    }

    if (normalizedEndpoint.includes('get_custom_builder_faqs')) {
        const faqType = requestConfig.body?.faq_type;
        let slug: string | undefined;

        if (faqType === 'category_page') {
            // Category URL: /computer-electronics → slugParts = ['computer-electronics']
            slug = vars.category_slug || undefined;
        } else if (faqType === 'merchant_detail') {
            // Store URL: /store/addmotor.com → slugParts = ['store', 'addmotor.com']
            // Skip the store prefix (slug[0]) — merchant slug is everything after it
            const slugParts = Array.isArray(context?.params?.slug)
                ? (context!.params!.slug as string[])
                : typeof context?.params?.slug === 'string'
                    ? [context.params.slug]
                    : [];
            slug = slugParts.length > 1 ? slugParts.slice(1).join('/') : undefined;
        }

        if (slug) {
            return {
                ...requestConfig,
                body: { ...(requestConfig.body ?? {}), slug },
            };
        }
    }

    return requestConfig;
};

const DynamicSectionComponent = async ({
    component,
    context,
}: DynamicSectionComponentProps) => {
    // Builder pages commonly keep their editable section children (including
    // the repeated CARD template) under `structure.components`. Use the same
    // source that ComponentRenderer uses for static sections, otherwise the
    // API is fetched but its repeated cards are never discovered/rendered.
    const components: ComponentNode[] | undefined = component.structure?.components ?? component.components;
    const styles = component.structure?.styles ?? component.styles;
    const { gridCols, gridGap, itemsLayout, apiEndpoint, apiConfig, totalCards } = component;
    const inlineStyles = convertStylesToInline(styles);
    const gridStyle = getCustomThemeItemsLayoutStyle(itemsLayout, gridCols, gridGap);
    const templateVars = getRouteTemplateVariables(context);
    const configuredRequest: ApiRequestConfig | undefined = apiConfig
        ? buildRequestConfig(apiConfig, templateVars) ?? (apiConfig as ApiRequestConfig)
        : apiEndpoint
            ? ({ apiEndpoint, method: 'POST', body: {} as Record<string, any> } as ApiRequestConfig)
            : undefined;
    const request = applyRouteAwareRequestOverrides(configuredRequest, context);
    const isFaqSection = !!request?.apiEndpoint?.includes('get_custom_builder_faqs');
    let items: any[] = [];
    let meta: any = null;
    let faqMessage: string | null = null;
    let dataFetchFailed = false;
    // Hoisted out of the try block so the repeated CARD's loadMore branch
    // below can read page 1's raw response for its `pagination` object,
    // without a second fetch.
    let response: any = null;

    if (request) {
        try {
            response = await fetchRawDataForRenderRequest(request, {
                companyId: context?.companyId,
                domain: context?.domain,
            });
            items = resolveCollection(response, component, context);
            meta = resolveSectionMeta(response, context);

            if (isFaqSection) {
                // Backend responds 200 with an embedded error when the slug doesn't match
                // faq_type (e.g. a category slug used with faq_type=merchant_detail).
                const originalMessage = (response as any)?.data?.original?.message;
                if (typeof originalMessage === 'string' && originalMessage.trim()) {
                    faqMessage = 'Slug not found';
                } else if (items.length === 0) {
                    faqMessage = 'FAQs are not available for this page yet.';
                }
            }
        } catch (error) {
            dataFetchFailed = true;
            console.error(`Error loading section data for ${component.type}:`, error);
        }
    }

    const repeatedCard = components?.find((child) => child.type === 'CARD' && child.repeated);
    // FAQ accordion layout uses FAQ_ITEM (not CARD) for its repeated row - FaqItemComponent
    // does its own internal mapping over the items array, so it's rendered separately below
    // rather than through the CARD grid loop.
    const repeatedFaqItem = components?.find((child) => child.type === 'FAQ_ITEM' && child.repeated);
    // Optional featured-center override for a 3-card row (e.g. triple hero banner):
    // same data source as the side cards, just a differently-styled template for the
    // middle position.
    const centerCard = components?.find((child) => child.type === 'CARD' && (child as any).isCenterCard);
    // Styles the Prev/1/2/…/Next row PaginatedCardList renders when
    // repeatedCard.pagination is set — a plain, non-repeated template node
    // (builderPart: "pagination"), same lookup pattern as centerCard above.
    const paginationTemplate = components?.find((child) => (child as any).builderPart === 'pagination');
    const cardLimit = repeatedCard ? repeatedCard.totalCards || totalCards : undefined;
    const renderedItems = repeatedCard
        ? cardLimit
            ? items.slice(0, cardLimit)
            : items
        : [];
    const emptyStateKind = repeatedCard && !dataFetchFailed && request && renderedItems.length === 0
        ? getEmptyStateKind(component)
        : undefined;
    const centerIndex = centerCard ? Math.floor(renderedItems.length / 2) : -1;
    const repeatedGridStyle = centerCard
        ? { ...gridStyle, gridTemplateColumns: '1fr 2fr 1fr' }
        : getCustomThemeItemsLayoutStyle(
            itemsLayout,
            repeatedCard?.gridCols ?? gridCols,
            repeatedCard?.gridGap ?? gridGap,
        );
    // Full-width hero slider: one slide visible at a time (dots to switch between them),
    // not the "up to 3 side by side" look other sliderEnabled sections (e.g. merchants) use.
    const isFullHeroSlider = component.type === 'HeroBanner' && component.sliderEnabled;
    const heroSlideHeight = isFullHeroSlider
        ? parseInt(String(repeatedCard?.styles?.height || ''), 10) || 450
        : undefined;
    const currentPage = Number(meta?.pagination?.current_page || templateVars.page || '1') || 1;
    const totalPages = Number(meta?.pagination?.last_page || 1) || 1;
    const paginationBaseUrl = buildAllStoresBaseUrl(context);

    if (emptyStateKind) {
        return (
            <section
                style={inlineStyles}
                className={[
                    'w-full',
                    component.type === 'HeroBanner' ? 'cb-hero-banner-section' : undefined,
                    isResponsiveSidebar(component) ? 'cb-responsive-sidebar' : undefined,
                ].filter(Boolean).join(' ')}
            >
                <EmptyStateComponent kind={emptyStateKind} />
            </section>
        );
    }

    return (
        <section
            style={inlineStyles}
            className={[
                'w-full',
                component.type === 'HeroBanner' ? 'cb-hero-banner-section' : undefined,
                isResponsiveSidebar(component) ? 'cb-responsive-sidebar' : undefined,
            ].filter(Boolean).join(' ')}
        >
            {components?.map((child) => {
                if (child.type === 'CARD' && (child.repeated || (child as any).isCenterCard)) {
                    return null;
                }
                if (child.type === 'FAQ_ITEM' && child.repeated) {
                    return null;
                }
                if (child === paginationTemplate) {
                    return null;
                }

                const childToRender =
                    component.type === 'MerchantsAlphabetSection' && child.builderPart === 'letters'
                        ? {
                              ...child,
                              components: child.components?.map((letter) => {
                                  const activeAlphabet = templateVars.alphabet === 'other'
                                      ? '#'
                                      : String(templateVars.alphabet || '').toUpperCase();
                                  const isActive =
                                      letter.activeRouteVariable === 'alphabet' &&
                                      String(letter.data || '').toUpperCase() === activeAlphabet;
                                  return isActive
                                      ? {
                                            ...letter,
                                            styles: {
                                                ...(letter.styles ?? {}),
                                                ...(letter.activeStyles ?? {}),
                                            },
                                        }
                                      : letter;
                              }),
                          }
                        : child;

                return (
                    <ComponentRenderer
                        key={childToRender.id}
                        component={childToRender}
                        data={meta}
                        context={context}
                    />
                );
            })}

            {isFaqSection && faqMessage ? (
                <div className="w-full px-4 py-10 text-center text-sm text-slate-500">
                    {faqMessage}
                </div>
            ) : (
                <>
                    {repeatedFaqItem && (
                        <ComponentRenderer
                            key={repeatedFaqItem.id}
                            component={repeatedFaqItem}
                            data={items}
                            context={context}
                        />
                    )}

                    {repeatedCard && (
                        repeatedCard.repeated && repeatedCard.loadMore && request ? (
                            <LoadMoreCardList
                                cardTemplate={repeatedCard}
                                initialItems={renderedItems}
                                initialResponse={response}
                                apiConfig={request}
                                gridStyle={repeatedGridStyle}
                                context={context}
                            />
                        ) : repeatedCard.repeated && repeatedCard.pagination && request ? (
                            <PaginatedCardList
                                cardTemplate={repeatedCard}
                                paginationTemplate={paginationTemplate}
                                initialItems={renderedItems}
                                initialResponse={response}
                                apiConfig={request}
                                gridStyle={repeatedGridStyle}
                                context={context}
                            />
                        ) : component.sliderEnabled ? (
                            <MerchantSectionSlider
                                items={renderedItems}
                                cardComponent={repeatedCard}
                                context={context}
                                slideInterval={component.slideInterval}
                                autoPlay={component.autoPlay}
                                totalCards={cardLimit}
                                slidesToShow={isFullHeroSlider ? 1 : undefined}
                                slideHeight={heroSlideHeight}
                                containerClassName={isFullHeroSlider ? 'cb-hero-banner-slider' : undefined}
                            />
                        ) : (
                    <div style={repeatedGridStyle} className={itemsLayout === 'flex-wrap' || itemsLayout === 'flex-nowrap' ? undefined : 'cb-responsive-grid'}>
                                {renderedItems.map((item, index) => {
                                    const cardTemplate = centerCard && index === centerIndex ? centerCard : repeatedCard;
                                    return (
                                        <ComponentRenderer
                                            key={`${cardTemplate.id}-${index}`}
                                            component={
                                                component.type === 'HeroBanner'
                                                    ? {
                                                        ...cardTemplate,
                                                        customClassName: [
                                                            cardTemplate.customClassName,
                                                            'cb-hero-banner-slide',
                                                        ].filter(Boolean).join(' '),
                                                    }
                                                    : cardTemplate
                                            }
                                            data={item}
                                            context={context}
                                        />
                                    );
                                })}
                            </div>
                        )
                    )}

                    {component.type === 'MerchantsAlphabetSection' && repeatedCard && renderedItems.length === 0 && (
                        <div className="mx-auto mt-10 max-w-7xl px-6 text-center" role="status">
                            <p className="m-0 text-base font-semibold text-slate-700">No stores found</p>
                            <p className="mt-1 text-sm text-slate-500">
                                There are no stores starting with “{templateVars.alphabet === 'other' ? '#' : templateVars.alphabet || 'A'}” yet.
                            </p>
                        </div>
                    )}
                </>
            )}

            {component.type === 'MerchantsAlphabetSection' && !repeatedCard?.pagination && totalPages > 1 && (
                <nav aria-label="Store pages" className="mt-16 flex items-center justify-center gap-7 border-t border-slate-100 pt-9">
                    <Link
                        href={currentPage <= 2 ? paginationBaseUrl : `${paginationBaseUrl}/page/${currentPage - 1}`}
                        aria-label="Previous page"
                        className={[
                            'inline-flex h-11 w-11 items-center justify-center rounded-full border text-2xl leading-none transition-colors',
                            currentPage === 1 ? 'pointer-events-none border-slate-100 text-slate-300' : 'border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-500',
                        ].join(' ')}
                    >
                        ‹
                    </Link>
                    <p className="m-0 text-center text-sm font-semibold text-slate-800">
                        Page {currentPage}<span className="mt-1 block text-xs font-normal text-slate-400">out of {totalPages}</span>
                    </p>
                    <Link
                        href={`${paginationBaseUrl}/page/${currentPage + 1}`}
                        aria-label="Next page"
                        className={[
                            'inline-flex h-11 w-11 items-center justify-center rounded-full border text-2xl leading-none transition-colors',
                            currentPage === totalPages ? 'pointer-events-none border-slate-100 text-slate-300' : 'border-orange-200 text-orange-500 hover:border-orange-400',
                        ].join(' ')}
                    >
                        ›
                    </Link>
                </nav>
            )}
        </section>
    );
};

export default DynamicSectionComponent;
